import { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '../../lib/api'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { useUserProfile, useUserPickHistory, useUserParlayHistory, useUserPropPickHistory, useUserBonusHistory, useHeadToHead } from '../../hooks/useUserProfile'
import { useAuth } from '../../hooks/useAuth'
import { useConnectionStatus, useSendConnectionRequest } from '../../hooks/useConnections'
import HeadToHeadDetailModal from '../feed/HeadToHeadDetailModal'
import { useUserHotTakes, useRemindHotTake } from '../../hooks/useHotTakes'
import RichContent from '../feed/RichContent'
import { timeAgo } from '../../lib/time'
import { lockScroll, unlockScroll } from '../../lib/scrollLock'
import { useMemo } from 'react'
import { getTier } from '../../lib/scoring'
import { formatRecordValueCompact } from '../../lib/recordFormat'
import { toast } from '../ui/Toast'
import TierBadge from '../ui/TierBadge'
import OGBadge from '../ui/OGBadge'
import LoadingSpinner from '../ui/LoadingSpinner'
import PickHistoryByMonth from './PickHistoryByMonth'
import { groupPropPicks, groupLineLabel } from '../../lib/groupPropPicks'
import SocialLinks from '../ui/SocialLinks'
import Avatar from '../ui/Avatar'
import PickModalRouter from '../picks/PickModalRouter'
import ParlayResultModal from '../picks/ParlayResultModal'
import PropDetailModal from '../picks/PropDetailModal'
import ReportModal from '../moderation/ReportModal'
import { useBlockUser } from '../../hooks/useBlocked'
import { getBackdropUrl } from '../../lib/backdropUrl'
import { useUserSettledFutures } from '../../hooks/useFutures'

function EventTypeBreakdown({ sportStats, parlays, propPicks, bonuses, futuresPicks, picks, onItemTap }) {
  const [expanded, setExpanded] = useState({})

  const parlayStats = useMemo(() => {
    const settled = (parlays || []).filter((p) => p.status === 'settled')
    if (!settled.length) return null
    const wins = settled.filter((p) => p.is_correct === true).length
    const losses = settled.filter((p) => p.is_correct === false).length
    const points = settled.reduce((sum, p) => sum + (p.points_earned || 0), 0)
    return { wins, losses, total: settled.length, points }
  }, [parlays])

  const propStats = useMemo(() => {
    const settled = (propPicks || []).filter((p) => p.status === 'settled')
    if (!settled.length) return null
    const wins = settled.filter((p) => p.is_correct === true).length
    const losses = settled.filter((p) => p.is_correct === false).length
    const points = settled.reduce((sum, p) => sum + (p.points_earned || 0), 0)
    return { wins, losses, total: settled.length, points }
  }, [propPicks])

  const futuresStats = useMemo(() => {
    const settled = (futuresPicks || []).filter((p) => p.status === 'settled')
    if (!settled.length) return null
    const wins = settled.filter((p) => p.is_correct === true).length
    const losses = settled.filter((p) => p.is_correct === false).length
    const points = settled.reduce((sum, p) => sum + (p.points_earned || 0), 0)
    return { wins, losses, total: settled.length, points }
  }, [futuresPicks])

  const settledFuturesList = useMemo(() => {
    return (futuresPicks || [])
      .filter((p) => p.status === 'settled')
      .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at))
  }, [futuresPicks])

  const leaguePoints = useMemo(() => {
    if (!bonuses?.length) return 0
    return bonuses.reduce((sum, b) => sum + (b.points || 0), 0)
  }, [bonuses])

  const settledPicksBySport = useMemo(() => {
    const map = {}
    for (const pick of (picks || [])) {
      if (pick.status !== 'settled') continue
      const key = pick.games?.sports?.key
      if (!key) continue
      if (!map[key]) map[key] = []
      map[key].push(pick)
    }
    // Sort each sport's picks by game start time descending
    for (const key of Object.keys(map)) {
      map[key].sort((a, b) => new Date(b.games?.starts_at) - new Date(a.games?.starts_at))
    }
    return map
  }, [picks])

  const settledParlays = useMemo(() => {
    return (parlays || [])
      .filter((p) => p.status === 'settled')
      .sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at))
  }, [parlays])

  // Collapsed to one row per player + market + game. Eight rungs of a Jameis
  // Winston ladder are eight near-identical lines differing only in the
  // half-point, which buries everything else the user did.
  const settledPropGroups = useMemo(() => {
    const settled = (propPicks || []).filter((p) => p.status === 'settled')
    return groupPropPicks(settled)
      .sort((a, b) => new Date(b.sortDate || 0) - new Date(a.sortDate || 0))
  }, [propPicks])

  const hasAnything = sportStats?.length > 0 || parlayStats || propStats || futuresStats || leaguePoints !== 0

  if (!hasAnything) return null

  function toggle(key) {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  function renderItemPoints(points) {
    return (
      <div className={`font-semibold text-sm shrink-0 ml-3 ${
        points > 0 ? 'text-correct' : points < 0 ? 'text-incorrect' : 'text-text-muted'
      }`}>
        {points > 0 ? '+' : ''}{points ?? 0}
      </div>
    )
  }

  return (
    <div className="mb-4">
      <h3 className="text-xs text-text-muted uppercase tracking-wider mb-3">Event Type</h3>
      <div className="space-y-2">
        {(sportStats || []).map((stat) => {
          const key = stat.sports?.key
          const isOpen = expanded[key]
          const sportPicks = (settledPicksBySport[key] || []).slice(0, 10)
          return (
            <div key={stat.id}>
              <button
                onClick={() => toggle(key)}
                className="w-full bg-bg-primary/50 backdrop-blur-sm border border-text-primary/20 rounded-lg px-4 py-3 flex items-center justify-between text-left"
              >
                <div className="flex items-center gap-2">
                  <span className={`text-xs text-text-muted transition-transform ${isOpen ? 'rotate-90' : ''}`}>&#9656;</span>
                  <span className="font-semibold text-sm">{stat.sports?.name}</span>
                  <span className="text-text-muted text-xs">
                    {stat.correct_picks}W-{stat.total_picks - stat.correct_picks}L
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  {stat.sport_rank && (
                    <span className="text-xs text-text-muted">#{stat.sport_rank}/{stat.sport_total_users}</span>
                  )}
                  <span className="text-xs text-text-muted">Streak: {stat.current_streak}</span>
                  <span className={`font-semibold text-sm ${stat.total_points >= 0 ? 'text-correct' : 'text-incorrect'}`}>{stat.total_points > 0 ? '+' : ''}{stat.total_points} pts</span>
                </div>
              </button>
              {isOpen && sportPicks.length > 0 && (
                <div className="mt-1 space-y-1">
                  {sportPicks.map((pick) => (
                    <div
                      key={pick.id}
                      className="bg-bg-primary rounded-lg border border-text-primary/20 px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-bg-card-hover active:bg-bg-card-hover transition-colors"
                      onClick={() => onItemTap?.('pick', pick.id)}
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-semibold truncate">{pick.games?.away_team} @ {pick.games?.home_team}</div>
                        <div className="text-xs text-text-muted truncate">Picked: {pick.picked_team === 'home' ? pick.games?.home_team : pick.games?.away_team}</div>
                      </div>
                      {renderItemPoints(pick.points_earned)}
                    </div>
                  ))}
                </div>
              )}
              {isOpen && sportPicks.length === 0 && (
                <div className="mt-1 bg-bg-primary rounded-lg border border-text-primary/20 px-4 py-3 text-xs text-text-muted text-center">
                  No picks in this sport yet — points contributed by league finishes (see Leagues below).
                </div>
              )}
            </div>
          )
        })}
        {parlayStats && (() => {
          const isOpen = expanded['parlays']
          const items = settledParlays.slice(0, 10)
          return (
            <div>
              <button
                onClick={() => toggle('parlays')}
                className="w-full bg-bg-primary/50 backdrop-blur-sm border border-text-primary/20 rounded-lg px-4 py-3 flex items-center justify-between text-left"
              >
                <div className="flex items-center gap-2">
                  <span className={`text-xs text-text-muted transition-transform ${isOpen ? 'rotate-90' : ''}`}>&#9656;</span>
                  <span className="font-semibold text-sm">Parlays</span>
                  <span className="text-text-muted text-xs">
                    {parlayStats.wins}W-{parlayStats.losses}L
                  </span>
                </div>
                <span className={`font-semibold text-sm ${parlayStats.points >= 0 ? 'text-correct' : 'text-incorrect'}`}>{parlayStats.points > 0 ? '+' : ''}{parlayStats.points} pts</span>
              </button>
              {isOpen && items.length > 0 && (
                <div className="mt-1 space-y-1">
                  {items.map((parlay) => {
                    const legs = parlay.parlay_legs || []
                    const legSummary = legs.map((l) => l.picked_team === 'home' ? l.games?.home_team : l.games?.away_team).join(', ')
                    return (
                      <div
                        key={parlay.id}
                        className="bg-bg-primary rounded-lg border border-text-primary/20 px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-bg-card-hover active:bg-bg-card-hover transition-colors"
                        onClick={() => onItemTap?.('parlay', parlay.id)}
                      >
                        <div className="min-w-0">
                          <div className="text-sm font-semibold truncate">{parlay.leg_count}-Leg Parlay</div>
                          <div className="text-xs text-text-muted truncate">{legSummary}</div>
                        </div>
                        {renderItemPoints(parlay.points_earned)}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })()}
        {propStats && (() => {
          const isOpen = expanded['props']
          const items = settledPropGroups.slice(0, 10)
          return (
            <div>
              <button
                onClick={() => toggle('props')}
                className="w-full bg-bg-primary/50 backdrop-blur-sm border border-text-primary/20 rounded-lg px-4 py-3 flex items-center justify-between text-left"
              >
                <div className="flex items-center gap-2">
                  <span className={`text-xs text-text-muted transition-transform ${isOpen ? 'rotate-90' : ''}`}>&#9656;</span>
                  <span className="font-semibold text-sm">Props</span>
                  <span className="text-text-muted text-xs">
                    {propStats.wins}W-{propStats.losses}L
                  </span>
                </div>
                <span className={`font-semibold text-sm ${propStats.points >= 0 ? 'text-correct' : 'text-incorrect'}`}>{propStats.points > 0 ? '+' : ''}{propStats.points} pts</span>
              </button>
              {isOpen && items.length > 0 && (
                <div className="mt-1 space-y-1">
                  {items.map((g) => (
                    <div
                      key={g.key}
                      className="bg-bg-primary rounded-lg border border-text-primary/20 px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-bg-card-hover active:bg-bg-card-hover transition-colors"
                      // Hands back the whole group. The detail modal renders
                      // the full ladder when there is more than one, and the
                      // first pick is the one comments hang off.
                      onClick={() => onItemTap?.('prop', g.picks[0].id, g.picks)}
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-semibold truncate">
                          {g.playerName} — {g.count > 1 ? g.marketLabel : `${g.picks[0].player_props?.line} ${g.marketLabel}`}
                        </div>
                        <div className="text-xs text-text-muted truncate">
                          {g.count > 1
                            ? `${g.count} picks · ${groupLineLabel(g)} · ${g.wins}W-${g.losses}L`
                            : `Picked: ${g.picks[0].picked_side}`}
                        </div>
                      </div>
                      {renderItemPoints(g.netPoints)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })()}
        {leaguePoints !== 0 && (() => {
          const isOpen = expanded['leagues']
          return (
            <div>
              <button
                onClick={() => toggle('leagues')}
                className="w-full bg-bg-primary/50 backdrop-blur-sm border border-text-primary/20 rounded-lg px-4 py-3 flex items-center justify-between text-left"
              >
                <div className="flex items-center gap-2">
                  <span className={`text-xs text-text-muted transition-transform ${isOpen ? 'rotate-90' : ''}`}>&#9656;</span>
                  <span className="font-semibold text-sm">Leagues</span>
                </div>
                <span className={`font-semibold text-sm ${leaguePoints >= 0 ? 'text-correct' : 'text-incorrect'}`}>{leaguePoints > 0 ? '+' : ''}{leaguePoints} pts</span>
              </button>
              {isOpen && bonuses?.length > 0 && (
                <div className="mt-1 space-y-1">
                  {bonuses.map((bonus) => (
                    <div
                      key={bonus.id}
                      className="bg-bg-primary rounded-lg border border-text-primary/20 px-4 py-3 flex items-center justify-between"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-semibold truncate">{bonus.label}</div>
                      </div>
                      {renderItemPoints(bonus.points)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })()}
        {futuresStats && (() => {
          const isOpen = expanded['futures']
          const items = settledFuturesList.slice(0, 10)
          return (
            <div>
              <button
                onClick={() => toggle('futures')}
                className="w-full bg-bg-primary/50 backdrop-blur-sm border border-text-primary/20 rounded-lg px-4 py-3 flex items-center justify-between text-left"
              >
                <div className="flex items-center gap-2">
                  <span className={`text-xs text-text-muted transition-transform ${isOpen ? 'rotate-90' : ''}`}>&#9656;</span>
                  <span className="font-semibold text-sm">Futures</span>
                  <span className="text-text-muted text-xs">
                    {futuresStats.wins}W-{futuresStats.losses}L
                  </span>
                </div>
                <span className={`font-semibold text-sm ${futuresStats.points >= 0 ? 'text-correct' : 'text-incorrect'}`}>{futuresStats.points > 0 ? '+' : ''}{futuresStats.points} pts</span>
              </button>
              {isOpen && items.length > 0 && (
                <div className="mt-1 space-y-1">
                  {items.map((fp) => (
                    <div
                      key={fp.id}
                      className="bg-bg-primary rounded-lg border border-text-primary/20 px-4 py-3 flex items-center justify-between"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-semibold truncate">{fp.futures_markets?.title || 'Futures'}</div>
                        <div className="text-xs text-text-muted truncate">Picked: {fp.picked_outcome}</div>
                      </div>
                      {renderItemPoints(fp.points_earned)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        })()}
      </div>
    </div>
  )
}

export default function UserProfileModal({ userId, onClose }) {
  const navigate = useNavigate()
  const { session } = useAuth()
  const { data: user, isLoading } = useUserProfile(userId)
  const { data: picks, isLoading: picksLoading } = useUserPickHistory(userId)
  const { data: parlays, isLoading: parlaysLoading } = useUserParlayHistory(userId)
  const { data: propPicks, isLoading: propsLoading } = useUserPropPickHistory(userId)
  // Every pick in the tapped ladder, so the detail modal can show the set
  // rather than one rung of it. Null for a single-pick row.
  const [selectedPropGroup, setSelectedPropGroup] = useState(null)
  const { data: bonuses, isLoading: bonusesLoading } = useUserBonusHistory(userId)
  const { data: futuresPicks } = useUserSettledFutures(userId)
  const [showH2HDetail, setShowH2HDetail] = useState(false)
  const isViewingOther = userId && session?.user?.id !== userId
  const isOwnProfile = !isViewingOther
  const queryClient = useQueryClient()

  // Backdrop drag-to-position (own profile only)
  const [draggingBackdrop, setDraggingBackdrop] = useState(false)
  const [previewBackdropY, setPreviewBackdropY] = useState(null) // displayed value while dragging
  const longPressTimer = useRef(null)
  const dragStart = useRef({ y: 0, baseY: 50 })

  function clearLongPressTimer() {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
  }

  function handleBackdropPointerDown(e) {
    if (!isOwnProfile || !user?.backdrop_image) return
    const startY = e.clientY
    const baseY = user.backdrop_y ?? 50
    longPressTimer.current = setTimeout(() => {
      setDraggingBackdrop(true)
      setPreviewBackdropY(baseY)
      dragStart.current = { y: startY, baseY }
      // Light haptic on devices that support it
      if (typeof navigator !== 'undefined' && navigator.vibrate) navigator.vibrate(20)
    }, 350)
  }

  function handleBackdropPointerMove(e) {
    if (!draggingBackdrop) {
      // If user moved more than 8px before long-press fired, treat as scroll → cancel
      if (longPressTimer.current && Math.abs(e.clientY - dragStart.current.y) > 8) {
        clearLongPressTimer()
      }
      return
    }
    e.preventDefault()
    const delta = e.clientY - dragStart.current.y
    // Sensitivity: 4px of finger movement → 1% shift (gentle)
    const next = Math.max(0, Math.min(100, dragStart.current.baseY + delta * 0.25))
    setPreviewBackdropY(next)
  }

  async function handleBackdropPointerUp() {
    clearLongPressTimer()
    if (!draggingBackdrop) return
    const finalY = Math.round(previewBackdropY)
    setDraggingBackdrop(false)
    setPreviewBackdropY(null)
    if (finalY === (user.backdrop_y ?? 50)) return // no change
    try {
      await api.patch('/users/me', { backdrop_y: finalY })
      queryClient.invalidateQueries({ queryKey: ['userProfile', userId] })
      queryClient.invalidateQueries({ queryKey: ['profile'] })
      toast('Backdrop position saved', 'success')
    } catch (err) {
      toast(err.message || 'Failed to save', 'error')
    }
  }
  const { data: h2h } = useHeadToHead(isViewingOther ? userId : null)
  const { data: connectionStatus } = useConnectionStatus(isViewingOther ? userId : null)
  const sendRequest = useSendConnectionRequest()
  const [justSent, setJustSent] = useState(false)
  const { data: hotTakes } = useUserHotTakes(userId)
  const remindHotTake = useRemindHotTake()
  const [selectedPickId, setSelectedPickId] = useState(null)
  const [selectedParlayId, setSelectedParlayId] = useState(null)
  const [selectedPropPickId, setSelectedPropPickId] = useState(null)
  const [showAllHotTakes, setShowAllHotTakes] = useState(false)
  const [showReportModal, setShowReportModal] = useState(false)
  const [confirmBlock, setConfirmBlock] = useState(false)
  const [showAvatarOverlay, setShowAvatarOverlay] = useState(false)
  const blockUser = useBlockUser()

  async function handleBlock() {
    try {
      await blockUser.mutateAsync(userId)
      toast(`@${user?.username} has been blocked`, 'success')
      onClose()
    } catch (err) {
      toast(err.message || 'Failed to block user', 'error')
    }
  }

  useEffect(() => {
    if (!userId) return
    lockScroll()
    return () => unlockScroll()
  }, [userId])

  async function handleConnect() {
    if (!user?.username) return
    try {
      await sendRequest.mutateAsync(user.username)
      setJustSent(true)
      toast(`Connection request sent to @${user.username}`, 'success')
    } catch (err) {
      toast(err.message || 'Failed to send request', 'error')
    }
  }

  async function handleRemind(hotTakeId) {
    try {
      await remindHotTake.mutateAsync({ hotTakeId })
      toast('Reminder posted to the feed', 'success')
    } catch (err) {
      toast(err.message || 'Failed to remind', 'error')
    }
  }

  const connStatus = justSent ? 'pending_sent' : connectionStatus?.status

  if (!userId) return null

  return (
    <>
    <div
      className="fixed inset-0 z-50 flex items-center justify-center px-4 md:py-6"
      style={{ paddingTop: 'max(1.5rem, calc(3.5rem + env(safe-area-inset-top) + 1rem))', paddingBottom: 'max(1.5rem, calc(3.5rem + env(safe-area-inset-bottom) + 1rem))' }}
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/60" />
      <div
        className="relative bg-bg-primary border border-text-primary/20 w-full max-w-md rounded-2xl max-h-full flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-0 right-0 z-20 text-text-muted hover:text-text-primary text-xl leading-none p-4"
        >
          &times;
        </button>
        <div className={`overflow-y-auto overscroll-contain px-6 pb-6 ${!user?.backdrop_image && !isLoading ? 'pt-10' : ''}`}>

        {isLoading ? (
          <LoadingSpinner />
        ) : !user ? (
          <p className="text-text-muted text-center pt-10">User not found</p>
        ) : (
          <>
            {/* Header */}
            {/* Avatar overlay */}
            {showAvatarOverlay && user.avatar_url && (
              <div
                className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center"
                onClick={() => setShowAvatarOverlay(false)}
              >
                <img
                  src={user.avatar_url}
                  alt=""
                  className="w-72 h-72 md:w-80 md:h-80 rounded-full object-cover"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            )}

            {/* User backdrop */}
            {user.backdrop_image && (
              <div className="relative -mx-6 mb-4 h-52 overflow-hidden">
                <img
                  src={getBackdropUrl(user.backdrop_image)}
                  alt=""
                  className="w-full h-full object-cover select-none"
                  style={{ objectPosition: `center ${(draggingBackdrop ? previewBackdropY : (user.backdrop_y ?? 50))}%` }}
                  draggable={false}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-bg-primary from-5% via-bg-primary/50 via-50% to-transparent pointer-events-none" />

                {/* Drag-to-position handle (own profile only) — covers the upper
                    65% of the backdrop, leaving the bottom for the avatar tap area */}
                {isOwnProfile && (
                  <div
                    className="absolute top-0 left-0 right-0 z-10 cursor-grab active:cursor-grabbing"
                    style={{ height: '65%', touchAction: 'none' }}
                    onPointerDown={handleBackdropPointerDown}
                    onPointerMove={handleBackdropPointerMove}
                    onPointerUp={handleBackdropPointerUp}
                    onPointerCancel={handleBackdropPointerUp}
                    onPointerLeave={handleBackdropPointerUp}
                  />
                )}

                {/* Hint that appears while dragging */}
                {draggingBackdrop && (
                  <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-bg-primary/90 backdrop-blur border border-accent/40 rounded-full px-3 py-1 text-[10px] uppercase tracking-wider text-accent font-bold pointer-events-none">
                    Drag to position · {Math.round(previewBackdropY)}%
                  </div>
                )}
              </div>
            )}

            {user.avatar_url ? (
              <div className={`flex flex-col items-center mb-4 ${user.backdrop_image ? '-mt-20 relative z-10' : ''}`}>
                <img
                  src={user.avatar_url}
                  alt=""
                  className="w-24 h-24 rounded-full object-cover mb-3 cursor-pointer"
                  onClick={() => setShowAvatarOverlay(true)}
                />
                <div className="font-display text-xl">{user.display_name || user.username}</div>
                <div className="text-text-muted text-sm">@{user.username}</div>
                <SocialLinks user={user} />
              </div>
            ) : (
              <div className="flex items-center gap-3 mb-4">
                <Avatar user={user} size="2xl" />
                <div className="min-w-0 flex-1">
                  <div className="font-display text-xl truncate">{user.display_name || user.username}</div>
                  <div className="text-text-muted text-sm">@{user.username}</div>
                  <SocialLinks user={user} />
                </div>
              </div>
            )}

            {/* Connection status */}
            {isViewingOther && connStatus === 'none' && (
              <button
                onClick={handleConnect}
                disabled={sendRequest.isPending}
                className="text-xs font-semibold px-4 py-1.5 rounded-full bg-accent text-white hover:bg-accent/90 disabled:opacity-50 mb-4"
              >
                {sendRequest.isPending ? '...' : 'Connect'}
              </button>
            )}
            {isViewingOther && connStatus === 'pending_sent' && (
              <span className="inline-block text-xs font-semibold px-4 py-1.5 rounded-full bg-bg-primary text-text-muted mb-4">
                Pending
              </span>
            )}
            {isViewingOther && connStatus === 'connected' && (
              <div className="flex items-center gap-2 mb-4">
                <span className="inline-block text-xs font-semibold px-4 py-1.5 rounded-full bg-bg-primary text-correct">
                  Connected
                </span>
                <button
                  onClick={() => { onClose(); navigate(`/messages/${userId}`) }}
                  className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-bg-primary text-text-secondary hover:text-accent transition-colors"
                  title="Message"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                </button>
              </div>
            )}

            {/* Bio */}
            {user.bio && (
              <p className="text-text-secondary text-sm mb-4">{user.bio}</p>
            )}

            {/* Sports Interests */}
            {user.sports_interests?.length > 0 && (
              <div className="flex items-center gap-1.5 mb-4">
                {user.sports_interests.map((emoji, i) => (
                  <span key={i} className="text-lg">{emoji}</span>
                ))}
              </div>
            )}

            {/* Tier + Points + Rank */}
            <div className="flex items-center gap-3 mb-2">
              <TierBadge tier={getTier(user.total_points).name} size="md" />
              <span className="font-display text-2xl text-accent">{user.total_points} pts</span>
              <span className="text-text-muted text-sm ml-auto">
                Rank #{user.rank} of {user.total_users}
              </span>
            </div>

            {/* OG badge — own row beneath the tier */}
            {user.is_og && (
              <div className="mb-4">
                <OGBadge size="sm" />
              </div>
            )}

            {/* Crowns */}
            {user.crowns?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {user.crowns.map((crown) => {
                  const title = user.title_preference === 'queen' ? 'Queen' : 'King'
                  const label = crown === 'I KNOW BALL'
                    ? `${title} of I KNOW BALL`
                    : `${crown} ${title}`
                  return (
                    <span
                      key={crown}
                      className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-accent text-xs font-semibold"
                    >
                      <span>👑</span>
                      {label}
                    </span>
                  )
                })}
              </div>
            )}

            {/* Records */}
            {user.records?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {user.records.map((record) => (
                  <span
                    key={record.key}
                    className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-yellow-500 text-xs font-semibold"
                  >
                    <span>🏆</span>
                    {record.label} — {formatRecordValueCompact(record)}
                  </span>
                ))}
              </div>
            )}

            {/* Pick Record */}
            <div className="bg-bg-primary/50 backdrop-blur-sm border border-text-primary/20 rounded-xl p-4 mb-4">
              <h3 className="text-xs text-text-muted uppercase tracking-wider mb-3">Pick Record</h3>
              {/* Win % counts only picks that WON or LOST.
                  user.record.total includes pushes — ties, postponed games,
                  voided props, anything with is_correct null — so dividing
                  by it treated every no-action pick as a loss. One user read
                  437W-362L at "50%", which is 437/869: the 70 pushes had
                  quietly cost him four and a half points. The real figure is
                  437/799, 55%.

                  Wins + Losses therefore will NOT equal Total for anyone
                  holding a push, and that is correct rather than an
                  oversight. A Pushes tile to close the gap was tried and
                  dropped — it gave a dead stat the same weight as the record
                  itself, to explain an arithmetic nobody was auditing. */}
              {(() => {
                const decided = user.record.wins + user.record.losses
                return (
                  <div className="grid grid-cols-4 gap-3 text-center">
                    <div>
                      <div className="font-display text-xl text-text-primary">{user.record.total}</div>
                      <div className="text-xs text-text-muted">Total</div>
                    </div>
                    <div>
                      <div className="font-display text-xl text-correct">{user.record.wins}</div>
                      <div className="text-xs text-text-muted">Wins</div>
                    </div>
                    <div>
                      <div className="font-display text-xl text-incorrect">{user.record.losses}</div>
                      <div className="text-xs text-text-muted">Losses</div>
                    </div>
                    <div>
                      <div className="font-display text-xl text-text-primary">
                        {decided > 0 ? `${((user.record.wins / decided) * 100).toFixed(0)}%` : '—'}
                      </div>
                      <div className="text-xs text-text-muted">Win %</div>
                    </div>
                  </div>
                )
              })()}
            </div>

            {/* Head-to-Head */}
            {h2h && h2h.total > 0 && (
              <div
                className="bg-bg-primary/50 backdrop-blur-sm border border-text-primary/20 rounded-xl p-4 mb-4 cursor-pointer hover:bg-text-primary/5 transition-colors"
                onClick={() => setShowH2HDetail(true)}
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xs text-text-muted uppercase tracking-wider">Head-to-Head</h3>
                  <span className="text-xs text-accent">View History &rarr;</span>
                </div>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div>
                    <div className="font-display text-xl text-correct">{h2h.wins}</div>
                    <div className="text-xs text-text-muted">Your Wins</div>
                  </div>
                  <div>
                    <div className="font-display text-xl text-incorrect">{h2h.losses}</div>
                    <div className="text-xs text-text-muted">Their Wins</div>
                  </div>
                  <div>
                    <div className="font-display text-xl text-text-primary">{h2h.ties}</div>
                    <div className="text-xs text-text-muted">Ties</div>
                  </div>
                </div>
                <div className="text-xs text-text-muted text-center mt-2">
                  {h2h.total} games in common
                </div>
              </div>
            )}

            {showH2HDetail && (
              <HeadToHeadDetailModal
                item={{ matchup: { userA: { userId: session?.user?.id }, userB: { userId } } }}
                onClose={() => setShowH2HDetail(false)}
              />
            )}

            {/* Pick History */}
            <div className="mb-4">
              <PickHistoryByMonth picks={picks} parlays={parlays} propPicks={propPicks} bonuses={bonuses} isLoading={picksLoading || parlaysLoading || propsLoading || bonusesLoading} allCollapsed onItemTap={(type, id, group) => {
                  if (type === 'pick') setSelectedPickId(id)
                  else if (type === 'parlay') setSelectedParlayId(id)
                  else if (type === 'prop') { setSelectedPropPickId(id); setSelectedPropGroup(group || null) }
                }} />
            </div>

            {/* Event Type Breakdown */}
            <EventTypeBreakdown sportStats={user.sport_stats} parlays={parlays} propPicks={propPicks} bonuses={bonuses} futuresPicks={futuresPicks} picks={picks} onItemTap={(type, id, group) => {
                  if (type === 'pick') setSelectedPickId(id)
                  else if (type === 'parlay') setSelectedParlayId(id)
                  else if (type === 'prop') { setSelectedPropPickId(id); setSelectedPropGroup(group || null) }
                }} />


            {/* H2H Matchups link */}
            {isViewingOther && h2h && h2h.total > 0 && (
              <button
                onClick={() => setShowH2HDetail(true)}
                className="w-full text-center text-sm text-accent hover:bg-accent/10 font-semibold py-3 mt-4 rounded-xl border border-accent/30 transition-colors"
              >
                See H2H Matchups
              </button>
            )}

            {/* View feed link */}
            <button
              onClick={() => {
                onClose()
                navigate(`/hub?tab=user_feeds&user=${userId}`)
              }}
              className="w-full text-center text-sm text-accent hover:bg-accent/10 font-semibold py-3 mt-3 rounded-xl border border-accent/30 transition-colors"
            >
              View {user.display_name || user.username}'s Feed
            </button>

            {/* Member since */}
            <div className="text-text-muted text-xs text-center mt-3">
              Member since {new Date(user.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </div>

            {/* Report / Block — other users only */}
            {isViewingOther && (
              <div className="mt-4 pt-3 border-t border-border flex items-center justify-center gap-4">
                <button
                  onClick={() => setShowReportModal(true)}
                  className="text-[11px] text-text-muted/60 hover:text-text-secondary transition-colors flex items-center gap-1"
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
                    <line x1="4" y1="22" x2="4" y2="15" />
                  </svg>
                  Report
                </button>
                {!confirmBlock ? (
                  <button
                    onClick={() => setConfirmBlock(true)}
                    className="text-[11px] text-text-muted/60 hover:text-incorrect transition-colors"
                  >
                    Block
                  </button>
                ) : (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handleBlock}
                      disabled={blockUser.isPending}
                      className="text-[11px] font-semibold text-incorrect hover:underline disabled:opacity-50"
                    >
                      {blockUser.isPending ? 'Blocking...' : 'Confirm Block'}
                    </button>
                    <button
                      onClick={() => setConfirmBlock(false)}
                      className="text-[11px] text-text-muted/60 hover:text-text-secondary"
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
        </div>
      </div>
    </div>
    <PickModalRouter pickId={selectedPickId} onClose={() => setSelectedPickId(null)} />
    <ParlayResultModal parlayId={selectedParlayId} onClose={() => setSelectedParlayId(null)} />
    <PropDetailModal
      propPickId={selectedPropPickId}
      groupPicks={selectedPropGroup}
      onClose={() => { setSelectedPropPickId(null); setSelectedPropGroup(null) }}
    />
    {showReportModal && (
      <ReportModal
        targetType="profile_picture"
        reportedUserId={userId}
        onClose={() => setShowReportModal(false)}
      />
    )}
    </>
  )
}
